import { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PricingTransparency = () => {
  const [selectedCategory, setSelectedCategory] = useState('emergency');

  const pricingCategories = {
    emergency: {
      title: "Emergency Services",
      icon: "Siren",
      color: "error",
      description: "24/7 emergency response with rapid dispatch and immediate problem resolution.",
      services: [
        {
          name: "Emergency Callout Fee",
          range: "R800 - R1,200",
          includes: ["Immediate dispatch", "Initial assessment", "First 30 minutes labor"],
          notes: "Waived if repair is completed"
        },
        {
          name: "Burst Pipe Repair",
          range: "R1,500 - R3,500",
          includes: ["Pipe replacement", "Leak sealing", "Pressure testing", "Cleanup"],
          notes: "Depends on pipe material and accessibility"
        },
        {
          name: "Emergency Drain Clearing",
          range: "R1,200 - R2,500",
          includes: ["High-pressure jetting", "Blockage removal", "Flow testing"],
          notes: "Additional charges for root removal or camera inspection"
        }
      ]
    },
    leak: {
      title: "Leak Detection & Repair",
      icon: "Droplets",
      color: "primary",
      description: "Advanced leak detection technology and professional repair services.",
      services: [
        {
          name: "Leak Detection Service",
          range: "R1,500 - R3,000",
          includes: ["Thermal imaging", "Acoustic detection", "Moisture mapping", "Detailed report"],
          notes: "Cost credited toward repair if work is approved"
        },
        {
          name: "Hidden Leak Repair",
          range: "R2,500 - R6,000",
          includes: ["Access creation", "Pipe repair/replacement", "Waterproofing", "Surface restoration"],
          notes: "Price varies with location and extent of damage"
        },
        {
          name: "Slab Leak Repair",
          range: "R5,000 - R12,000",
          includes: ["Precision detection", "Minimal excavation", "Pipe repair", "Concrete restoration"],
          notes: "Trenchless options available at premium"
        }
      ]
    },
    installation: {
      title: "Installation Services",
      icon: "Wrench",
      color: "accent",
      description: "Professional installation of fixtures, appliances, and complete plumbing systems.",
      services: [
        {
          name: "Fixture Installation",
          range: "R800 - R2,500",
          includes: ["Fixture mounting", "Connection", "Sealing", "Testing"],
          notes: "Sink, toilet, or shower - fixture not included"
        },
        {
          name: "Water Heater Installation",
          range: "R3,500 - R8,000",
          includes: ["Old unit removal", "New unit installation", "Venting setup", "Safety testing"],
          notes: "Unit cost separate - gas or electric"
        },
        {
          name: "Complete Bathroom Plumbing",
          range: "R15,000 - R35,000",
          includes: ["All fixtures", "Pipe installation", "Drainage system", "Testing & certification"],
          notes: "New construction or renovation - fixtures extra"
        }
      ]
    },
    maintenance: {
      title: "Maintenance & Inspection",
      icon: "ClipboardCheck",
      color: "success",
      description: "Preventive maintenance and comprehensive inspection services.",
      services: [
        {
          name: "Annual Maintenance",
          range: "R1,200 - R2,500",
          includes: ["Full system inspection", "Drain cleaning", "Valve testing", "Performance report"],
          notes: "Residential property - commercial rates higher"
        },
        {
          name: "Pre-Purchase Inspection",
          range: "R1,500 - R3,000",
          includes: ["Comprehensive assessment", "Photo documentation", "Detailed report", "Repair estimates"],
          notes: "Essential for property transactions"
        },
        {
          name: "Water Quality Testing",
          range: "R800 - R2,000",
          includes: ["Sample collection", "Laboratory testing", "Results interpretation", "Recommendations"],
          notes: "Treatment system installation quoted separately"
        }
      ]
    }
  };

  const currentCategory = pricingCategories?.[selectedCategory];

  const colorClasses = {
    error: "bg-error/10 text-error border-error",
    primary: "bg-primary/10 text-primary border-primary",
    accent: "bg-accent/10 text-accent border-accent",
    success: "bg-success/10 text-success border-success"
  };

  const pricingFactors = [
    {
      icon: "Clock",
      title: "Time of Service",
      description: "After-hours and weekend emergency calls may include premium rates."
    },
    {
      icon: "MapPin",
      title: "Location",
      description: "Travel time and distance from our base may affect callout fees."
    },
    {
      icon: "Tool",
      title: "Complexity",
      description: "Difficult access, specialized equipment, or extensive damage affects pricing."
    },
    {
      icon: "Package",
      title: "Materials",
      description: "Quality materials and fixtures are quoted separately from labor costs."
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-success/10 px-4 py-2 rounded-full mb-4">
            <Icon name="DollarSign" size={20} color="var(--color-success)" />
            <span className="text-sm font-semibold text-success">Transparent Pricing</span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Fair & Honest Pricing
          </h2>
          <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            No hidden fees or surprise charges. Here's what you can expect to pay for our professional plumbing services.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {Object.entries(pricingCategories)?.map(([key, category]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 ${
                selectedCategory === key
                  ? `${colorClasses?.[category?.color]} border-2 shadow-md`
                  : 'bg-card border-2 border-border text-foreground hover:border-primary'
              }`}
            >
              <Icon name={category?.icon} size={20} />
              <span className="font-semibold">{category?.title}</span>
            </button>
          ))}
        </div>

        <div className="bg-card rounded-2xl p-8 md:p-12 border border-border shadow-lg mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border-2 ${colorClasses?.[currentCategory?.color]}`}>
              <Icon name={currentCategory?.icon} size={32} color="currentColor" />
            </div>
            <div>
              <h3 className="font-headline text-2xl md:text-3xl font-bold text-foreground">
                {currentCategory?.title}
              </h3>
              <p className="text-muted-foreground">{currentCategory?.description}</p>
            </div>
          </div>

          <div className="space-y-6">
            {currentCategory?.services?.map((service, index) => (
              <div 
                key={index}
                className="bg-muted/30 rounded-xl p-6 border border-border"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h4 className="font-headline text-xl font-semibold text-foreground mb-2">
                      {service?.name}
                    </h4>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="DollarSign" size={18} color="var(--color-accent)" />
                      <span className="font-accent text-lg font-bold text-accent">
                        {service?.range}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                      Includes:
                    </h5>
                    <ul className="space-y-2">
                      {service?.includes?.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Icon name="Check" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Info" size={16} color="var(--color-primary)" />
                      Important Notes:
                    </h5>
                    <p className="text-sm text-muted-foreground">{service?.notes}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <h3 className="font-headline text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
            Factors Affecting Pricing
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingFactors?.map((factor, index) => (
              <div 
                key={index}
                className="bg-card rounded-xl p-6 border border-border text-center"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name={factor?.icon} size={24} color="var(--color-primary)" />
                </div>
                <h4 className="font-headline text-lg font-semibold text-foreground mb-2">
                  {factor?.title}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {factor?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-8 md:p-12 text-center text-primary-foreground">
          <h3 className="font-headline text-2xl md:text-3xl font-bold mb-4">
            Get an Accurate Quote
          </h3>
          <p className="font-body text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            Every job is unique. Call us for a free, no-obligation quote tailored to your specific needs. We'll provide a detailed breakdown before any work begins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:0827935785"
              className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-4 rounded-lg font-semibold transition-colors emergency-cta"
            >
              <Icon name="Phone" size={20} />
              <span>Call for Quote: 082 793 5785</span>
            </a>
          </div>
          <p className="text-sm opacity-80 mt-4">
            Free quotes • No hidden fees • Upfront pricing
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingTransparency;